import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    FaSearch, FaPlus, FaEdit, FaTrash,
    FaUserNinja, FaCheckCircle, FaTimesCircle, FaClock,
    FaExclamationTriangle, FaMars, FaVenus
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/axios';

const getEntryMembers = (item) => {
    if (!item) return [];

    if (Array.isArray(item.entry_members)) {
        return item.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof item.entry_members === 'string' && item.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(item.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return item.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (item) => {
    const members = getEntryMembers(item);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return item?.name || '-';
};

const getGenderLabel = (gender) => {
    if (gender === 'Lelaki' || gender === 'L') return 'Lelaki / Putra';
    if (gender === 'Perempuan' || gender === 'P') return 'Perempuan / Putri';
    if (gender === 'Campuran') return 'Campuran';
    return gender || '-';
};

const ManagerAthletePoomsae = () => {
    const { type } = useParams();

    const currentType = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Prestasi';
    const isFestival = currentType === 'Festival';

    const [athletes, setAthletes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterGender, setFilterGender] = useState('');

    const [deleteModal, setDeleteModal] = useState({ show: false, uuid: null });

    const BACKEND_URL = process.env.REACT_APP_BACKEND || '';
    const IMG_PATH = `${BACKEND_URL}/uploads/athletes/`;

    const fetchMyAthletes = async () => {
        setIsLoading(true);

        try {
            const response = await api.get('/athletes/my-athletes', {
                params: {
                    competition_type: 'Poomsae',
                    type: currentType
                }
            });

            let data = [];

            if (Array.isArray(response.data)) {
                data = response.data;
            } else if (response.data.result && Array.isArray(response.data.result)) {
                data = response.data.result;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                data = response.data.data;
            }

            const filteredData = data.filter(item => (
                item.competition_type === 'Poomsae' &&
                item.type === currentType
            ));

            setAthletes(filteredData);
        } catch (error) {
            toast.error("Gagal mengambil data atlet Poomsae.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMyAthletes();
        setSearchTerm('');
        setFilterStatus('');
        setFilterGender('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentType]);

    const confirmDelete = (uuid) => {
        setDeleteModal({ show: true, uuid });
    };

    const executeDelete = async () => {
        try {
            await api.delete(`/athletes/${deleteModal.uuid}`);
            toast.success("Atlet berhasil dihapus!");
            setAthletes(prev => prev.filter(item => item.uuid !== deleteModal.uuid));
            setDeleteModal({ show: false, uuid: null });
        } catch (error) {
            toast.error(error.response?.data?.msg || "Gagal menghapus atlet");
            setDeleteModal({ show: false, uuid: null });
        }
    };

    const filteredAthletes = athletes.filter(item => {
        const search = searchTerm.toLowerCase();
        const membersText = getEntryMembers(item).join(' ').toLowerCase();

        const matchSearch =
            (item.name || '').toLowerCase().includes(search) ||
            membersText.includes(search) ||
            (item.division || '').toLowerCase().includes(search) ||
            (item.poomsae_category || '').toLowerCase().includes(search) ||
            (item.belt || '').toLowerCase().includes(search);

        const matchStatus = filterStatus === '' ? true : item.payment_status === filterStatus;
        const matchGender = filterGender === '' ? true : item.gender === filterGender;

        return matchSearch && matchStatus && matchGender;
    });

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                        <FaUserNinja className="text-primary" /> Atlet Poomsae {currentType}
                    </h4>
                    <p className="text-muted small mb-0">
                        Total <b>{filteredAthletes.length}</b> entry terdaftar di kategori ini.
                    </p>
                </div>

                <Link
                    to="/manager/atlet/tambah"
                    state={{ preselectedCompType: 'Poomsae', preselectedType: currentType }}
                    className="btn btn-primary rounded-pill px-4 py-2 shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2 hover-scale"
                >
                    <FaPlus /> Daftarkan Entry
                </Link>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                {/* TOOLBAR */}
                <div className="card-header bg-white p-4 border-bottom-0">
                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <div className="input-group shadow-sm rounded-pill overflow-hidden h-100">
                                <span className="input-group-text bg-light border-0 text-muted">
                                    <FaSearch />
                                </span>
                                <input
                                    type="text"
                                    className="form-control bg-light border-0 py-2"
                                    placeholder="Cari nama, kategori, divisi, atau sabuk..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-6 col-md-3">
                            <select
                                className="form-select w-100 bg-light border-0 shadow-sm rounded-pill py-2 fw-medium text-muted h-100"
                                value={filterGender}
                                onChange={(e) => setFilterGender(e.target.value)}
                            >
                                <option value="">Semua Gender</option>
                                <option value="Lelaki">Lelaki / Putra</option>
                                <option value="Perempuan">Perempuan / Putri</option>
                                {!isFestival && <option value="Campuran">Campuran</option>}
                            </select>
                        </div>

                        <div className="col-6 col-md-3">
                            <select
                                className="form-select w-100 bg-light border-0 shadow-sm rounded-pill py-2 fw-medium text-muted h-100"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="">Semua Status</option>
                                <option value="Verified">Lunas</option>
                                <option value="Pending">Menunggu</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="card-body p-0">
                    {isLoading ? (
                        <div className="text-center py-5 my-5">
                            <div className="spinner-border text-primary mb-3" role="status"></div>
                            <p className="text-muted fw-medium">Memuat data atlet Poomsae...</p>
                        </div>
                    ) : filteredAthletes.length === 0 ? (
                        <div className="text-center py-5 my-5 text-muted">
                            <FaUserNinja size={50} className="mb-3 opacity-25" />
                            <h6 className="fw-bold">Tidak ada entry ditemukan</h6>
                            <p className="small">Coba ubah kata kunci pencarian atau daftarkan entry baru di kategori ini.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-nowrap">
                                <thead className="table-light text-muted small text-uppercase">
                                    <tr>
                                        <th className="ps-4 py-3" width="5%">No</th>
                                        <th className="py-3">Info Entry</th>
                                        {!isFestival && <th className="py-3">Kategori</th>}
                                        {isFestival && <th className="py-3">Sabuk</th>}
                                        <th className="py-3">Divisi</th>
                                        <th className="py-3 text-center">Status Bayar</th>
                                        <th className="py-3 text-end pe-4">Aksi</th>
                                    </tr>
                                </thead>

                                <tbody className="border-top-0">
                                    {filteredAthletes.map((item, index) => {
                                        const members = getEntryMembers(item);
                                        const displayName = getDisplayName(item);

                                        return (
                                            <tr key={item.uuid} className="hover-up-row">
                                                <td className="ps-4 fw-bold text-muted border-bottom-0">{index + 1}</td>

                                                <td className="border-bottom-0">
                                                    <div className="d-flex align-items-center gap-3 py-2">
                                                        <div className="rounded-circle overflow-hidden bg-light border" style={{ width: '45px', height: '45px' }}>
                                                            <img
                                                                src={item.image ? `${IMG_PATH}${item.image}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'Entry')}&background=random`}
                                                                alt={displayName}
                                                                className="w-100 h-100 object-fit-cover"
                                                                onError={(e) => {
                                                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'Entry')}&background=random`;
                                                                }}
                                                            />
                                                        </div>

                                                        <div>
                                                            <h6 className="mb-0 fw-bold text-dark">{displayName}</h6>
                                                            <span className="small text-muted d-flex align-items-center gap-1 flex-wrap">
                                                                {item.gender === 'Lelaki' ? <FaMars className="text-primary" /> : <FaVenus className={item.gender === 'Campuran' ? 'text-purple' : 'text-danger'} />}
                                                                {getGenderLabel(item.gender)}
                                                                {members.length > 1 && (
                                                                    <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill ms-1">
                                                                        {members.length} Nama
                                                                    </span>
                                                                )}
                                                                <span>• Lahir: {item.birth_year}</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {!isFestival && (
                                                    <td className="border-bottom-0 fw-medium">
                                                        <span className={`badge rounded-pill px-3 py-2 ${
                                                            item.poomsae_category === 'Freestyle'
                                                                ? 'bg-primary bg-opacity-10 text-primary border border-primary'
                                                                : item.poomsae_category === 'Pair'
                                                                    ? 'bg-warning bg-opacity-10 text-warning border border-warning'
                                                                    : item.poomsae_category === 'Team'
                                                                        ? 'bg-success bg-opacity-10 text-success border border-success'
                                                                        : 'bg-secondary bg-opacity-10 text-secondary border border-secondary'
                                                        }`}>
                                                            {item.poomsae_category || '-'}
                                                        </span>
                                                    </td>
                                                )}

                                                {isFestival && (
                                                    <td className="border-bottom-0 fw-medium text-capitalize">
                                                        {item.belt || '-'}
                                                    </td>
                                                )}

                                                <td className="border-bottom-0">
                                                    <h6 className="mb-0 small fw-bold text-dark">{item.division || '-'}</h6>
                                                </td>

                                                <td className="text-center border-bottom-0">
                                                    {item.payment_status === 'Verified' && (
                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill px-3 py-1">
                                                            <FaCheckCircle className="me-1" /> Lunas
                                                        </span>
                                                    )}
                                                    {item.payment_status === 'Pending' && (
                                                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning rounded-pill px-3 py-1">
                                                            <FaClock className="me-1" /> Menunggu
                                                        </span>
                                                    )}
                                                    {item.payment_status === 'Rejected' && (
                                                        <span className="badge bg-danger bg-opacity-10 text-danger border border-danger rounded-pill px-3 py-1">
                                                            <FaTimesCircle className="me-1" /> Ditolak
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="text-end pe-4 border-bottom-0">
                                                    <Link to={`/manager/atlet/edit/${item.uuid}`} className="btn btn-sm btn-light text-primary rounded-circle hover-scale me-2 shadow-sm" title="Edit">
                                                        <FaEdit />
                                                    </Link>
                                                    <button
                                                        onClick={() => confirmDelete(item.uuid)}
                                                        className="btn btn-sm btn-light text-danger rounded-circle hover-scale shadow-sm"
                                                        title="Hapus"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* DELETE MODAL */}
            {deleteModal.show && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg rounded-4 p-4 text-center" style={{ maxWidth: '400px', width: '90%', animation: 'popIn 0.3s ease-out' }}>
                        <div className="mb-3">
                            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3">
                                <FaExclamationTriangle size={40} />
                            </div>
                        </div>
                        <h5 className="fw-bold text-dark">Hapus Data Entry?</h5>
                        <p className="text-muted small mb-4">Tindakan ini bersifat permanen. Segala riwayat pendaftaran entry ini akan ikut terhapus. Lanjutkan?</p>
                        <div className="d-flex justify-content-center gap-2">
                            <button className="btn btn-light fw-bold rounded-pill px-4" onClick={() => setDeleteModal({ show: false, uuid: null })}>Batal</button>
                            <button className="btn btn-danger fw-bold rounded-pill px-4 shadow-sm" onClick={executeDelete}>Ya, Hapus Permanen</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.1); }
                .hover-up-row { transition: background-color 0.2s; }
                .hover-up-row:hover { background-color: #f8f9fa !important; }
                .text-purple { color: #6f42c1; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes popIn { 
                    0% { transform: scale(0.9); opacity: 0; } 
                    100% { transform: scale(1); opacity: 1; } 
                }
            `}</style>
        </div>
    );
};

export default ManagerAthletePoomsae;